import { useEffect, useRef, useState } from "react";
import { BsSearch } from "react-icons/bs";
import { RiArrowDownSFill } from "react-icons/ri";
import { RxCross1 } from "react-icons/rx";
import Cookies from "js-cookie";
import { format } from "date-fns";
import { MdDevices } from "react-icons/md";
import baseURL from "../../config/api";
import Pagination from "../../components/Pagination";
import useDelete from "../../hook/useDelete";
import useGroup from "../../hook/useGroup";
import { formatDateTimeForDisplay } from "../../utils/getUserDateFormat";
import useAuth from "../../hook/useAuth";
import { delete_BulkDevice } from "../../services/Devices";

const heading = [
    "Actions",
    "Device Id",
    "UserName",
    "Last Logged In",
    "Active?",
];

const LoginDevices = () => {
    const token = Cookies.get("token-my-cdir");
    const { selectedGroupId }: any = useGroup();
    const { auth }: any = useAuth();
    const userId = auth?.id;
    const { setDeletePopup, setData: setDeleteData }: any = useDelete();

    const [searchValue, setSearchValue] = useState<string>("");
    const [displayFilter, setDisplayFilter] = useState<boolean>(false);
    const [deviceFilterValue, setDeviceFilterValue] = useState(0);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [searchTableData, setSearchTableData] = useState<any[]>([]);

    const [paginationModel, setPaginationModel] = useState<{
        page: number;
        size: number;
    }>({
        page: 0,
        size: 10,
    });

    const [totalLength, setTotalLength] = useState<number>(0);
    const [selectedFilter, setSelectedFilter] = useState<string>("UserName");
    const [isFirstTime, setIsFirstTime] = useState<boolean>(true);
    const [showEntries, setShowEntries] = useState<number>(10);
    const [isRefresh, setIsRefresh] = useState<boolean>(true);
    const [selectedDeviceIds, setSelectedDeviceIds] = useState<number[]>([]);
    const selectAllRef = useRef<HTMLInputElement>(null);

    const visibleDevices = searchTableData.slice(0, showEntries);

    const allVisibleSelected =
        visibleDevices.length > 0 &&
        visibleDevices.every((device) =>
            selectedDeviceIds.includes(Number(device.id))
        );

    const someVisibleSelected =
        visibleDevices.some((device) =>
            selectedDeviceIds.includes(Number(device.id))
        );

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate =
                someVisibleSelected && !allVisibleSelected;
        }
    }, [someVisibleSelected, allVisibleSelected]);

    useEffect(() => {
        setSelectedDeviceIds([]);
    }, [
        paginationModel.page,
        paginationModel.size,
        searchValue,
        deviceFilterValue,
        selectedGroupId,
    ]);

    useEffect(() => {
        if (!isFirstTime) {
            setSelectedFilter("User Name");
            setDeviceFilterValue(0);

            if (searchValue !== "") {
                setSearchValue("");
                setIsFirstTime(false);
            } else {
                setPaginationModel({
                    page: 0,
                    size: 10,
                });
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGroupId]);

    useEffect(() => {
        if (searchValue === "" && !isFirstTime) {
            const newPaginationModel = {
                page: 0,
                size: paginationModel.size,
            };

            setPaginationModel(newPaginationModel);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    useEffect(() => {
        if (searchValue === "") {
            setIsFirstTime(false);
            apiCall(paginationModel);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel]);

    const apiCall = (newPaginationModel: {
        page: number;
        size: number;
    }) => {
        if (selectedGroupId > 0) {
            setIsDataLoaded(true);

            fetch(
                `${baseURL}device/GetDeviceReportPaging/${selectedGroupId},${newPaginationModel.page},${newPaginationModel.size}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            )
                .then((res) => res.json())
                .then((data) => {
                    const result = data.deviceReport.map((item: any) => {
                        return {
                            ...item,
                            isActive: item.isActive ? "Yes" : "No",
                            lastLogedIn: item.lastLogedIn
                                ? format(
                                    new Date(item.lastLogedIn),
                                    "MMM dd,yyyy ii:mm a"
                                )
                                : "",
                        };
                    });

                    setSearchTableData(result);
                    setTotalLength(data.count);
                    setIsDataLoaded(false);
                })
                .catch((error) => {
                    console.log(error);
                    setIsDataLoaded(false);
                    setSearchTableData([]);
                    setTotalLength(0);
                });
        } else {
            setIsDataLoaded(false);
            setSearchTableData([]);
            setTotalLength(0);
        }
    };

    useEffect(() => {
        let timeOutId: NodeJS.Timeout;

        if (searchValue !== "") {
            timeOutId = setTimeout(() => {
                const newPaginationModel = {
                    page: 0,
                    size: paginationModel.size,
                };

                setPaginationModel(newPaginationModel);
            }, 1000);
        }

        return () => clearTimeout(timeOutId);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    useEffect(() => {
        if (searchValue !== "") {
            filteApiCall(paginationModel);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel]);

    const filteApiCall = (newPaginationModel: {
        page: number;
        size: number;
    }) => {
        let filterData: any[] = [];

        setIsDataLoaded(true);

        switch (deviceFilterValue) {
            case 0:
                fetch(
                    `${baseURL}device/GetDeviceReportSearchWithUsername/${searchValue},${selectedGroupId},${newPaginationModel.page},${newPaginationModel.size}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                )
                    .then((res) => res.json())
                    .then((data) => {
                        filterData = data.deviceReport.map((item: any) => {
                            return {
                                ...item,
                                isActive: item.isActive ? "Yes" : "No",
                                lastLogedIn: item.lastLogedIn
                                    ? format(
                                        new Date(item.lastLogedIn),
                                        "MMM dd,yyyy ii:mm a"
                                    )
                                    : "",
                            };
                        });

                        setSearchTableData(filterData);
                        setTotalLength(data.count);
                        setIsDataLoaded(false);
                    })
                    .catch((error) => {
                        console.log(error);
                        setIsDataLoaded(false);
                        setSearchTableData([]);
                        setTotalLength(0);
                    });

                break;
        }
    };

    const applyFilter = () => {
        setSelectedFilter("User Name");
        setSearchValue("");
        setDisplayFilter(false);
    };

    const clearFilter = () => {
        setSearchValue("");
        setDisplayFilter(false);
    };

    const handleSelectDevice = (id: number) => {
        setSelectedDeviceIds((previous) =>
            previous.includes(id)
                ? previous.filter((deviceId) => deviceId !== id)
                : [...previous, id]
        );
    };

    const handleSelectAllDevices = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const visibleIds = visibleDevices.map((device) =>
            Number(device.id)
        );

        if (event.target.checked) {
            setSelectedDeviceIds((previous) =>
                Array.from(new Set(previous.concat(visibleIds)))
            );
        } else {
            setSelectedDeviceIds((previous) =>
                previous.filter((id) => !visibleIds.includes(id))
            );
        }
    };

    const deleteBulkDevices = async (ids: number[]) => {
        return delete_BulkDevice(
            ids,
            userId,
            token!.toString()
        );
    };

    const deleteDevice = (primaryKey: number) => {
        setDeleteData({
            id: primaryKey,
            tilte: "Delete",
            summary: "Are you sure you want to delete this Device?",
            isDelete: (callback: any) => {
                deleteBulkDevices([primaryKey])
                    .then(() => {
                        setSelectedDeviceIds((previous) =>
                            previous.filter((id) => id !== primaryKey)
                        );

                        setIsRefresh(true);
                        setSearchValue("");
                        callback();
                    })
                    .catch((error) => {
                        console.error(error);
                        alert(error.message || "Failed to delete device.");
                    });
            },
        });

        setDeletePopup(true);
    };

    const handleBulkDelete = () => {
        if (selectedDeviceIds.length === 0) return;

        const idsToDelete = [...selectedDeviceIds];

        setDeleteData({
            id: idsToDelete,
            tilte: "Delete Devices",
            summary: `Are you sure you want to delete selected devices?`,
            isDelete: (callback: any) => {
                deleteBulkDevices(idsToDelete)
                    .then(() => {
                        setSelectedDeviceIds([]);
                        setIsRefresh(true);
                        setSearchValue("");
                        callback();
                    })
                    .catch((error) => {
                        console.error("Bulk delete failed:", error);
                        alert(
                            error.message ||
                            "Failed to delete selected devices."
                        );
                    });
            },
        });

        setDeletePopup(true);
    };

    useEffect(() => {
        if (isRefresh) {
            apiCall(paginationModel);
            setIsRefresh(false);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRefresh, paginationModel]);

    return (
        <section className="user__container p-3 xm:p-4 overflow-x-hidden">

            <div className="min-w-full flex flex-col xm:flex-row justify-between items-start xm:items-center gap-3">
                <h1 className="px-2 xm:px-4 py-1.5 flex items-center gap-2">
                    <MdDevices fontSize={20} />
                    <span className="text-base xm:text-lg">Devices</span>
                </h1>
            </div>

            <div className="flex flex-col xm:flex-row gap-3 justify-between xm:items-center border border-black p-2 mt-4">
                <div className="relative flex flex-col xm:flex-row gap-3 items-start xm:items-center w-full">
                    <div className="relative w-full xm:w-auto">
                        <button
                            className="bg-secondary text-white px-3 py-2 xm:py-1 rounded-md flex items-center justify-between w-full xm:w-auto whitespace-nowrap"
                            onClick={() =>
                                setDisplayFilter(!displayFilter)
                            }
                        >
                            {selectedFilter}
                            <RiArrowDownSFill className="ml-1" />
                        </button>

                        <div
                            className={`${displayFilter ? "block" : "hidden"
                                } absolute z-10 w-full xm:w-40 p-2 bg-white border border-black mt-2 xm:mt-0 xm:top-0 xm:left-full`}
                        >
                            <div className="flex justify-between">
                                <label className="text-xs font-bold">
                                    Fields
                                </label>

                                <div
                                    onClick={() => setDisplayFilter(false)}
                                    className="w-4 h-4 cursor-pointer text-sm text-center"
                                >
                                    <RxCross1 />
                                </div>
                            </div>

                            <select
                                className="w-full text-xs py-1 border mt-1"
                                value={deviceFilterValue}
                                onChange={(e) =>
                                    setDeviceFilterValue(
                                        parseInt(e.target.value)
                                    )
                                }
                            >
                                <option value="0">User Name</option>
                            </select>

                            <div className="flex mt-2 justify-between">
                                <button
                                    className="text-xs bg-secondary text-white py-1 px-2 rounded-md mt-3 w-full mr-1 whitespace-nowrap"
                                    onClick={() => applyFilter()}
                                >
                                    Apply Filter
                                </button>

                                <button
                                    className="text-xs bg-secondary text-white py-1 px-2 rounded-md mt-3 w-full ml-1"
                                    onClick={() => clearFilter()}
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 w-full border px-2 py-1 rounded xm:border-none">
                        <div className="h-4 w-4 text-gray-900">
                            <BsSearch />
                        </div>

                        <input
                            type="text"
                            placeholder="Search…"
                            className="w-full text-sm xm:text-lg pl-2 outline-none"
                            value={searchValue}
                            onChange={(e) =>
                                setSearchValue(e.target.value)
                            }
                        />
                    </div>
                </div>
            </div>

            {selectedDeviceIds.length > 0 && (
                <div className="flex flex-col xm:flex-row items-center justify-between gap-3 mt-3 mb-2">
                    <span className="text-sm text-gray-600">
                        {selectedDeviceIds.length}{" "}
                        {selectedDeviceIds.length === 1
                            ? "device"
                            : "devices"}{" "}
                        selected
                    </span>

                    <button
                        type="button"
                        onClick={handleBulkDelete}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm"
                    >
                        Delete Selected ({selectedDeviceIds.length})
                    </button>
                </div>
            )}

            <div className="mt-2" />

            {!isDataLoaded ? (
                <>

                    <div className="w-full overflow-x-auto">
                        <table className="min-w-[700px] w-full bg-white">
                            <thead className="bg-gray-200 whitespace-nowrap">
                                <tr>

                                    <th className="px-4 xm:px-6 py-2 text-center">
                                        <input
                                            ref={selectAllRef}
                                            type="checkbox"
                                            checked={allVisibleSelected}
                                            onChange={handleSelectAllDevices}
                                            className="h-4 w-4 cursor-pointer accent-blue-600"
                                        />
                                    </th>

                                    {heading.map((item, index) => (
                                        <th
                                            key={index}
                                            className="px-4 xm:px-6 py-2 text-left text-xs xm:text-sm font-semibold text-black"
                                        >
                                            {item}
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody className="whitespace-nowrap">
                                {visibleDevices.length > 0 ? (
                                    visibleDevices.map((item, index) => (
                                        <tr
                                            key={item.id}
                                            className={
                                                selectedDeviceIds.includes(
                                                    Number(item.id)
                                                )
                                                    ? "bg-blue-100"
                                                    : "even:bg-blue-50"
                                            }
                                        >

                                            <td className="px-4 xm:px-6 py-2 text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedDeviceIds.includes(
                                                        Number(item.id)
                                                    )}
                                                    onChange={() =>
                                                        handleSelectDevice(
                                                            Number(item.id)
                                                        )
                                                    }
                                                    className="h-4 w-4 cursor-pointer accent-blue-600"
                                                />
                                            </td>

                                            <td className="px-4 xm:px-6 py-2">
                                                <button
                                                    className="mr-4"
                                                    onClick={() =>
                                                        deleteDevice(item.id)
                                                    }
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="w-5 fill-red-500 hover:fill-red-700"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            d="M19 7a1 1 0 0 0-1 1v11.191A1.92 1.92 0 0 1 15.99 21H8.01A1.92 1.92 0 0 1 6 19.191V8a1 1 0 0 0-2 0v11.191A3.918 3.918 0 0 0 8.01 23h7.98A3.918 3.918 0 0 0 20 19.191V8a1 1 0 0 0-1-1Zm1-3h-4V2a1 1 0 0 0-1-1H9a1 1 0 0 0-1 1v2H4a1 1 0 0 0 0 2h16a1 1 0 0 0 0-2ZM10 4V3h4v1Z"
                                                            data-original="#000000"
                                                        />
                                                        <path
                                                            d="M11 17v-7a1 1 0 0 0-2 0v7a1 1 0 0 0 2 0Zm4 0v-7a1 1 0 0 0-2 0v7a1 1 0 0 0 2 0Z"
                                                            data-original="#000000"
                                                        />
                                                    </svg>
                                                </button>
                                            </td>

                                            <td className="px-4 xm:px-6 py-2 text-xs xm:text-sm">
                                                {item.deviceId}
                                            </td>

                                            <td className="px-4 xm:px-6 py-2 text-xs xm:text-sm">
                                                {item.userName}
                                            </td>

                                            <td className="px-4 xm:px-6 py-2 text-xs xm:text-sm">
                                                {formatDateTimeForDisplay(
                                                    item.lastLogedIn
                                                )}
                                            </td>

                                            <td className="px-4 xm:px-6 py-2 text-xs xm:text-sm">
                                                {item.isActive}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={6}
                                            className="px-2 py-1 text-sm text-center h-52"
                                        >
                                            No data available
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {totalLength > 0 && (
                        <div className="mt-6 flex flex-col xm:flex-row items-center justify-between gap-3">
                            <Pagination
                                pageOption={[
                                    5, 10, 15, 20, 25, 50, 75, 100,
                                ]}
                                totalLength={totalLength}
                                paginationModel={paginationModel}
                                setPaginationModel={setPaginationModel}
                                showEntries={showEntries}
                                setShowEntries={setShowEntries}
                            />
                        </div>
                    )}
                </>
            ) : (
                <div className="min-w-full h-[300px] xm:h-[480px] flex justify-center items-center">
                    <div className="flex flex-col items-center">
                        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-current border-r-transparent text-secondary"></div>
                        <span className="text-sm mt-2">Loading...</span>
                    </div>
                </div>
            )}
        </section>
    );
};

export default LoginDevices;